import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Generate time options in 15-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      options.push(`${formattedHour}:${formattedMinute}`);
    }
  }
  return options;
};

// Format time for display (12-hour format with AM/PM)
const formatTimeDisplay = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

const SchedulePostDialog = ({ isOpen, onClose, onSchedule, initialDate, initialTime }) => {
  const [scheduledDate, setScheduledDate] = useState(initialDate || new Date());
  const [scheduledTime, setScheduledTime] = useState(initialTime || "12:00");
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        document.body.style.overflow = ''; // Re-enable scrolling
      }, 200);
    }

    return () => {
      document.body.style.overflow = ''; // Ensure scrolling is re-enabled on unmount
    };
  }, [isOpen]);

  // If not open and not in the process of closing animation, don't render at all
  if (!isOpen && !isAnimating) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create a combined date time from the date picker and time input
    const [hours, minutes] = scheduledTime.split(":");
    const scheduledDateTime = new Date(scheduledDate);
    scheduledDateTime.setHours(parseInt(hours, 10));
    scheduledDateTime.setMinutes(parseInt(minutes, 10));
    
    onSchedule(scheduledDateTime);
  };

  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div 
      className={`schedule-dialog-overlay modal-overlay ${isOpen ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-dialog-title"
      id="scheduleDialogOverlay"
    >
      <div 
        className={`schedule-dialog-content modal-content ${isOpen ? 'scale-100' : 'scale-95'}`}
        onClick={handleModalClick}
        id="scheduleDialogContent"
      >
        <div className="schedule-dialog-header flex justify-between items-center mb-4" id="scheduleDialogHeader">
          <h3 className="schedule-dialog-title text-lg font-semibold" id="schedule-dialog-title">Schedule Post</h3>
          <button 
            onClick={onClose}
            className="schedule-dialog-close-btn text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close"
            id="scheduleDialogCloseBtn"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="schedule-dialog-form space-y-4" id="scheduleDialogForm">
          <div className="schedule-dialog-date-group" id="scheduleDateGroup">
            <label className="schedule-dialog-date-label block text-sm font-medium text-gray-700 mb-1" id="scheduleDateLabel">Date</label>
            <DatePicker
              selected={scheduledDate}
              onChange={date => setScheduledDate(date)}
              className="schedule-dialog-date-input w-full border rounded p-2"
              minDate={new Date()}
              dateFormat="MMMM d, yyyy"
              id="scheduleDatePicker"
            />
          </div>
          
          <div className="schedule-dialog-time-group" id="scheduleTimeGroup">
            <label className="schedule-dialog-time-label block text-sm font-medium text-gray-700 mb-1" id="scheduleTimeLabel">Time</label>
            <div className="schedule-dialog-time-select-container relative" id="scheduleTimeSelectContainer">
              <select
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="schedule-dialog-time-select w-full border rounded p-2 appearance-none cursor-pointer"
                id="scheduleTimeSelect"
              >
                {generateTimeOptions().map(time => (
                  <option key={time} value={time} className="schedule-time-option">
                    {formatTimeDisplay(time)}
                  </option>
                ))}
              </select>
              <div className="schedule-time-select-arrow pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700" id="scheduleTimeSelectArrow">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div className="schedule-dialog-actions pt-4 flex justify-end space-x-3" id="scheduleDialogActions">
            <button
              type="button"
              onClick={onClose}
              className="schedule-dialog-cancel-btn px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              id="scheduleDialogCancelBtn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="schedule-dialog-submit-btn px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              id="scheduleDialogSubmitBtn"
            >
              Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchedulePostDialog;